"use client";

import { useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { EmblaCarouselType } from "embla-carousel";
import {
  NextButton,
  PrevButton,
  usePrevNextButtons,
} from "./Carousel/CarouselButtons";

type Props = {
  /** Les slides, deja enveloppees chacune dans leur element de slide. */
  children: React.ReactNode;
  className?: string;
  viewportClassName?: string;
  containerClassName?: string;
  /** Delai du defilement automatique, en ms (defaut embla : 4000). */
  autoplayDelay?: number;
};

/**
 * Ilot client des carrousels : seule la mecanique embla (defilement, boutons)
 * vit ici. Les slides sont rendues par le serveur et passees en `children`, si
 * bien que leur contenu (RichText Lexical, liens resolus) ne traverse pas la
 * frontiere client sous forme de JSON et reste dans le HTML initial.
 */
export function EmblaCarousel({
  children,
  className,
  viewportClassName,
  containerClassName,
  autoplayDelay,
}: Props) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [
    Autoplay(autoplayDelay ? { delay: autoplayDelay } : undefined),
  ]);

  // Une navigation manuelle arrete le defilement automatique.
  const onNavButtonClick = useCallback((emblaApi: EmblaCarouselType) => {
    emblaApi?.plugins()?.autoplay?.stop();
  }, []);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi, onNavButtonClick);

  return (
    <div className={className}>
      <div className={viewportClassName} ref={emblaRef}>
        <div className={containerClassName}>{children}</div>
      </div>
      <div className="embla__controls">
        <div className="embla__buttons">
          <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} />
          <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} />
        </div>
      </div>
    </div>
  );
}
